import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const PremiumHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Monthly Rates", href: "/monthly-rates" },
    { label: "Corporate Housing", href: "/corporate-housing" },
    { label: "Snowbird Rentals", href: "/snowbird-rentals" },
    { label: "Gallery", href: "/gallery" },
    { label: "Location", href: "/location" },
    { label: "Contact", href: "/contact" },
  ];

  const isHomePage = location.pathname === "/" || location.pathname === "/cinematic-preview";

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0A1628] text-white py-2 hidden md:block">
        <div className="container-premium flex justify-between items-center">
          <p className="text-xs font-body tracking-wide">
            Luxury Furnished Rentals · 90-Day Minimum · Indialantic, Florida
          </p>
          <div className="flex items-center gap-6">
            <a href="tel:4074509182" className="flex items-center gap-2 text-xs hover:text-[#C9A962] transition-colors">
              <Phone className="w-3 h-3" />
              (407) 450-9182
            </a>
            <a href="mailto:RAEVenturesLLC@gmail.com" className="flex items-center gap-2 text-xs hover:text-[#C9A962] transition-colors">
              <Mail className="w-3 h-3" />
              RAEVenturesLLC@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? "bg-white shadow-sm border-b border-[#EDE7DB]"
            : "bg-transparent"
        }`}
        data-testid="header"
      >
        <div className="container-premium">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo">
              <div className={`transition-colors ${isScrolled || !isHomePage ? 'text-[#0A1628]' : 'text-white'}`}>
                <span className="font-display text-2xl font-medium tracking-tight block leading-none">Beach Escape</span>
                <span className="text-[10px] font-body uppercase tracking-[0.3em] opacity-70 block mt-1">Indialantic · Florida</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`font-body text-sm font-medium transition-colors link-underline ${
                    isScrolled || !isHomePage ? "text-[#0A1628]" : "text-white"
                  } ${location.pathname === link.href ? "opacity-100" : "opacity-80 hover:opacity-100"}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact">
                <Button
                  className={`px-6 py-2.5 text-sm font-medium rounded-none transition-all ${
                    isScrolled || !isHomePage
                      ? "bg-[#0A1628] text-white hover:bg-[#1E3A5F]"
                      : "bg-white text-[#0A1628] hover:bg-[#F7F3ED]"
                  }`}
                  data-testid="header-cta-btn"
                >
                  Request Quote
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${isScrolled || !isHomePage ? "text-[#0A1628]" : "text-white"}`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-[#EDE7DB] py-6">
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="font-body text-base font-medium text-[#0A1628] hover:bg-[#F7F3ED] px-4 py-3 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="px-4 pt-4 border-t border-[#EDE7DB] mt-4">
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#0A1628] text-white hover:bg-[#1E3A5F] rounded-none py-4">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default PremiumHeader;
